import type { AxiosRequestConfig } from 'axios';
import { createApi } from '@reduxjs/toolkit/query/react';

import { axios } from '~/modules/axios';
import { TOOLBOX_API_URL } from '~/lib/environment';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';

/** The shape RTK Query hands to a page when a request fails. */
export interface QueryError {
  status?: number;
  data?: unknown;
  message?: string;
}

/** sysbay's reply envelope; most endpoints return `result`. */
export interface ApiEnvelope<T> {
  status?: number;
  message?: string;
  detail?: string;
  meta?: { bayToken?: string };
  result: T;
}

export const TAG_TYPES = [
  'Home',
  'Mls',
  'MlsStats',
  'MlsView',
  'MlsConfig',
  'Service',
  'ServiceStats',
  'ServiceHost',
  'Customer',
  'CustomerAgents',
  'AgreementMls',
  'AgreementCustomer',
  'Membership',
  'Credential',
  'AgreementDocument',
  'AgreementHistory',
  'Expiring',
  'Environment',
  'Product',
  'Attribute',
  'Flag',
  'Update',
  'Request',
  'SdkKey',
  'FlagUser',
  'FlagDashboard',
  'VideoStats',
  'VideoChart',
  'VideoJob',
  'DomainSetup',
  'Onboarding',
  'Auth',
] as const;

export type TagType = (typeof TAG_TYPES)[number];

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

/**
 * Axios transport for RTK Query. Keeping axios means the cookie token, `meta.bayToken`
 * rotation and the 401 redirect stay in `modules/axios.ts` instead of being reimplemented
 * around `fetchBaseQuery`. A request may use an absolute URL (account/listing services).
 */
export const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string }) =>
  async (args: AxiosRequestConfig) => {
    try {
      const url = args.url ?? '';
      const response = await axios({
        ...args,
        timeout: args.timeout ?? 180000,
        url: isAbsoluteUrl(url) ? url : `${baseUrl}${url}`,
      });
      return { data: response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return {
          error: {
            status: error.response?.status,
            data: error.response?.data ?? { message: error.message },
            message: error.message,
          },
        };
      }

      return { error: { message: error instanceof Error ? error.message : 'Request failed' } };
    }
  };

/** Unwraps sysbay's `{result}`. AI endpoints return their own body and never use this. */
export const unwrapResult = <T>(body: unknown): T => (body as ApiEnvelope<T>).result;

/** Human-readable text for an RTK Query error, with the page's own fallback as the last resort. */
export const queryErrorMessage = (error: unknown): string | undefined =>
  apiErrorMessage(error as ApiErrorInput);

export const toolboxApi = createApi({
  reducerPath: 'toolboxApi',
  baseQuery: axiosBaseQuery({ baseUrl: TOOLBOX_API_URL }),
  tagTypes: [...TAG_TYPES],
  endpoints: () => ({}),
});
